import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { selectCurrentGroupId } from './groups_slice';
import { parseTimespanFilter, parseAgeFilter } from "../../_helpers/misc_functions";
import { fetchInsightData } from "./api";

/*
REPORTED BURNOUT
 */
export const fetchRollingBurnoutMetrics = createAsyncThunk(
  'insights/fetchRollingBurnoutMetrics',
  async (thunkArg, { getState, rejectWithValue }) => {
    const state = getState();
    const { start_date, end_date } = parseTimespanFilter('1 Year');
    const filters = {
      start_date,
      end_date,
      practice_group_id: selectCurrentGroupId(state)
    };
    const endpoint = "reported_burnout/mode";
    try {
      return await fetchInsightData({ endpoint, filters });
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch reported burnout data');
    }
  },
  {
    condition: (thunkArg, { getState }) => {
      const { insights } = getState();
      const { status } = insights.reportedBurnout.rollingBurnoutMetrics;
      if (status === 'loading') {
        console.log('Rolling Burnout Metric is already loading. Skipping fetch.');
        return false;
      }
      return true;
    },
  }
);

export const fetchOverallBurnoutMetrics = createAsyncThunk(
  'insights/fetchOverallBurnoutMetrics',
  async (thunkArg, { getState, rejectWithValue }) => {
    const state = getState();
    const filters = state.insights.reportedBurnout.overallBurnoutMetrics.filters;
    const { start_date, end_date } = parseTimespanFilter(filters.timespan);
    const { start_age, end_age } = parseAgeFilter(filters.age);

    const allFilters = {
      ...filters,
      practice_group_id: selectCurrentGroupId(state),
      start_date,
      end_date,
      start_age,
      end_age
    };
    const endpoint = "reported_burnout/filtered";
    try {
      return await fetchInsightData({ endpoint, filters: allFilters });
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch reported burnout data');
    }
  },
  {
    condition: (thunkArg, { getState }) => {
      const { insights } = getState();
      const { status } = insights.reportedBurnout.overallBurnoutMetrics;
      if (status === 'loading') {
        console.log('Overall Burnout Metric is already loading. Skipping fetch.');
        return false;
      }
      return true;
    },
  }
);

export const fetchTopStressors = createAsyncThunk(
  'insights/fetchTopStressors',
  async (thunkArg, { getState, rejectWithValue }) => {
    const state = getState();
    const filters = state.insights.reportedBurnout.overallBurnoutMetrics.filters;
    const { start_date, end_date } = parseTimespanFilter(filters.timespan);
    const { start_age, end_age } = parseAgeFilter(filters.age);

    const allFilters = {
      ...filters,
      practice_group_id: selectCurrentGroupId(state),
      start_date,
      end_date,
      start_age,
      end_age,
      base_question_id: [
        parseInt(process.env.REACT_APP_1ST_STRESSOR),
        parseInt(process.env.REACT_APP_2ND_STRESSOR),
        parseInt(process.env.REACT_APP_3RD_STRESSOR)
      ]
    };
    const endpoint = "reported_burnout/stressors";
    try {
      return await fetchInsightData({ endpoint, filters: allFilters });
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch reported burnout data');
    }
  },
  {
    condition: (thunkArg, { getState }) => {
      const { insights } = getState();
      const { status } = insights.reportedBurnout.topStressors;
      if (status === 'loading') {
        console.log('Top Stressors are already loading. Skipping fetch.');
        return false;
      }
      return true;
    },
  }
);

export const fetchSignificantBurnoutMetrics = createAsyncThunk(
  'insights/reported_burnout/significant',
  async (thunkArg, { getState, rejectWithValue }) => {
    const state = getState();
    const filters = state.insights.reportedBurnout.significantBurnoutMetrics.filters;
    const { start_date, end_date } = parseTimespanFilter(filters.timespan);

    const allFilters = {
      practice_group_id: selectCurrentGroupId(state),
      start_date,
      end_date
    };
    const endpoint = "reported_burnout/significant";
    try {
      return await fetchInsightData({ endpoint, filters: allFilters });
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch reported burnout data');
    }
  },
  {
    condition: (thunkArg, { getState }) => {
      const { insights } = getState();
      const { status } = insights.reportedBurnout.significantBurnoutMetrics;
      if (status === 'loading') {
        console.log('Significant Burnout Metric is already loading. Skipping fetch.');
        return false;
      }
      return true;
    },
  }
);

/*
ENGAGEMENT
 */
export const fetchRollingEngagementHours = createAsyncThunk(
  'insights/fetchRollingEngagementHours',
  async (thunkArg, { getState, rejectWithValue }) => {
    const state = getState();
    const { start_date, end_date } = parseTimespanFilter('90 Days');
    const filters = {
      practice_group_id: selectCurrentGroupId(state),
      start_date,
      end_date
    };
    const endpoint = "engagement/hours_watched";
    try {
      return await fetchInsightData({ endpoint, filters });
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch rolling engagement data');
    }
  },
  {
    condition: (thunkArg, { getState }) => {
      const { insights } = getState();
      const { status } = insights.engagement.rollingEngagementMetrics.totalHoursWatched;
      if (status === 'loading') {
        console.log('Engagement Hours is already loading. Skipping fetch.');
        return false;
      }
      return true;
    },
  }
);

export const fetchRollingEngagementMicrolearnings = createAsyncThunk(
  'insights/fetchEngagementMicrolearnings',
  async (thunkArg, { getState, rejectWithValue }) => {
    const state = getState();
    const { start_date, end_date } = parseTimespanFilter('90 Days');
    const filters = {
      practice_group_id: selectCurrentGroupId(state),
      start_date,
      end_date
    };
    const endpoint = "engagement/total_watched";
    try {
      return await fetchInsightData({ endpoint, filters });
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch rolling engagement microlearnings data');
    }
  },
  {
    condition: (thunkArg, { getState }) => {
      const { insights } = getState();
      const { status } = insights.engagement.rollingEngagementMetrics.totalMicrolearningsWatched;
      if (status === 'loading') {
        console.log('Engagement Microlearnings is already loading. Skipping fetch.');
        return false;
      }
      return true;
    },
  }
);

export const fetchRollingEngagementCreditsClaimed = createAsyncThunk(
  'insights/fetchEngagementCreditsClaimed',
  async (thunkArg, { getState, rejectWithValue }) => {
    const state = getState();
    const { start_date, end_date } = parseTimespanFilter('90 Days');
    const filters = {
      practice_group_id: selectCurrentGroupId(state),
      start_date,
      end_date
    };
    const endpoint = "engagement/total_credits_claimed";
    try {
      return await fetchInsightData({ endpoint, filters });
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch rolling engagement microlearnings data');
    }
  },
  {
    condition: (thunkArg, { getState }) => {
      const { insights } = getState();
      const { status } = insights.engagement.rollingEngagementMetrics.totalCreditsClaimed;
      if (status === 'loading') {
        console.log('Engagement Credits Claimed is already loading. Skipping fetch.');
        return false;
      }
      return true;
    },
  }
);

export const fetchRollingEngagementPopularSeries = createAsyncThunk(
  'insights/fetchEngagementPopularSeries',
  async (thunkArg, { getState, rejectWithValue }) => {
    const state = getState();
    const { start_date, end_date } = parseTimespanFilter('90 Days');
    const filters = {
      practice_group_id: selectCurrentGroupId(state),
      start_date,
      end_date
    };
    const endpoint = "engagement/most_watched_series";
    try {
      return await fetchInsightData({ endpoint, filters });
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch rolling engagement popular series');
    }
  },
  {
    condition: (thunkArg, { getState }) => {
      const { insights } = getState();
      const { status } = insights.engagement.rollingEngagementMetrics.mostPopular;
      if (status === 'loading') {
        console.log('Engagement Most Popular series is already loading. Skipping fetch.');
        return false;
      }
      return true;
    },
  }
);

export const fetchEngagementMostWatchedMicrolearnings = createAsyncThunk(
  'insights/fetchEngagementMostWatchedMicrolearnings',
  async (thunkArg, { getState, rejectWithValue }) => {
    const state = getState();
    const filters = state.insights.engagement.filters;
    const { start_date, end_date } = parseTimespanFilter(filters.timeSpan);
    const { start_age, end_age } = parseAgeFilter(filters.age);
    const allFilters = {
      practice_group_id: selectCurrentGroupId(state),
      ...filters,
      start_date,
      end_date,
      start_age,
      end_age,
    };
    const endpoint = "engagement/most_watched_microlearnings";
    try {
      return await fetchInsightData({ endpoint, filters: allFilters });
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch engagement most watched microlearnings');
    }
  },
  {
    condition: (thunkArg, { getState }) => {
      const { insights } = getState();
      const { status } = insights.engagement.mostWatched;
      if (status === 'loading') {
        console.log('Engagement Most Watched Microlearnings is already loading. Skipping fetch.');
        return false;
      }
      return true;
    },
  }
);

export const fetchEngagementMostClaimedMicrolearnings = createAsyncThunk(
  'insights/fetchEngagementMostClaimed',
  async (thunkArg, { getState, rejectWithValue }) => {
    const state = getState();
    const filters = state.insights.engagement.filters;
    const { start_date, end_date } = parseTimespanFilter(filters.timeSpan);
    const { start_age, end_age } = parseAgeFilter(filters.age);
    const allFilters = {
      ...filters,
      practice_group_id: selectCurrentGroupId(state),
      start_date,
      end_date,
      start_age,
      end_age,
    };
    const endpoint = "engagement/most_claimed_microlearnings";
    try {
      return await fetchInsightData({ endpoint, filters: allFilters });
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch engagement most claimed');
    }
  },
  {
    condition: (thunkArg, { getState }) => {
      const { insights } = getState();
      const { status } = insights.engagement.mostClaimed;
      if (status === 'loading') {
        console.log('Engagement Most Claimed is already loading. Skipping fetch.');
        return false;
      }
      return true;
    },
  }
);

/*
EFFICACY
 */
export const fetchEfficacyBaseline = createAsyncThunk(
  'insights/fetchEfficacyBaseline',
  async (thunkArg, { getState, rejectWithValue }) => {
    const state = getState();
    const practice_group_id = selectCurrentGroupId(state);
    const filters = { practice_group_id, correlation_window: "1 Month" };
    const endpoint = `efficacy/correlation`;
    try {
      return await fetchInsightData({ endpoint, filters });
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch efficacy baseline');
    }
  },
  {
    condition: (thunkArg, { getState }) => {
      const { insights } = getState();
      const { status } = insights.efficacy.baselineCorrelation;
      return status !== 'loading';
    },
  }
);

export const fetchEfficacyAccrued = createAsyncThunk(
  'insights/fetchEfficacyAccrued',
  async (thunkArg, { getState, rejectWithValue }) => {
    const state = getState();
    const practice_group_id = selectCurrentGroupId(state);
    const filters = { practice_group_id, correlation_window: "4-6 Months", };
    const endpoint = `efficacy/correlation`;
    try {
      return await fetchInsightData({ endpoint, filters });
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch efficacy accrued');
    }
  },
  {
    condition: (thunkArg, { getState }) => {
      const { insights } = getState();
      const { status } = insights.efficacy.accruedCorrelation;
      return status !== 'loading';
    },
  }
);

export const fetchEfficacyUsersRegistered = createAsyncThunk(
  'insights/fetchEfficacyUsersRegistered',
  async (thunkArg, { getState, rejectWithValue }) => {
    const state = getState();
    const practice_group_id = selectCurrentGroupId(state);
    let filters = state.insights.efficacy.userMetrics.filters;
    const { start_date, end_date } = parseTimespanFilter(filters.timespan);
    const { start_age, end_age } = parseAgeFilter(filters.age);
    filters = {
      ...filters,
      start_date,
      end_date,
      start_age,
      end_age,
      practice_group_id
    };
    const endpoint = `efficacy/users_registered`;
    try {
      return await fetchInsightData({ endpoint, filters });
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch efficacy users registered');
    }
  },
  {
    condition: (thunkArg, { getState }) => {
      const { insights } = getState();
      const { status } = insights.efficacy.userMetrics.usersRegistered;
      return status !== 'loading';
    },
  }
);

export const fetchEfficacyMicrolearningsWatched = createAsyncThunk(
  'insights/fetchEfficacyMicrolearningsWatched',
  async (thunkArg, { getState, rejectWithValue }) => {
    const state = getState();
    const practice_group_id = selectCurrentGroupId(state);
    let filters = state.insights.efficacy.userMetrics.filters;
    const { start_date, end_date } = parseTimespanFilter(filters.timespan);
    const { start_age, end_age } = parseAgeFilter(filters.age);
    filters = {
      ...filters,
      start_date,
      end_date,
      start_age,
      end_age,
      practice_group_id
    };
    const endpoint = `efficacy/microlearnings`;
    try {
      return await fetchInsightData({ endpoint, filters });
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch efficacy MICROLEARNINGS VIEWED');
    }
  },
  {
    condition: (thunkArg, { getState }) => {
      const { insights } = getState();
      const { status } = insights.efficacy.userMetrics.microlearningsWatched;
      return status !== 'loading';
    },
  }
);

const initialState = {
  reportedBurnout: {
    rollingBurnoutMetrics: {
      data: null,
      status: 'idle',
      error: null,
      filters: {
        timespan: '90 days'
      },
    },
    overallBurnoutMetrics: {
      data: null,
      status: 'idle',
      error: null,
      filters: {
        timespan: '1 Year',
        age: 'All',
        gender: 'All',
        department: 'All',
        role: 'All',
        years_in_profession: 'All'
      }
    },
    topStressors: {
      comments: [],
      topStressors: {},
      totalStressors: 0,
      status: 'idle',
      error: null,
    },
    significantBurnoutMetrics: {
      data: null,
      status: 'idle',
      error: null,
      filters: {
        timespan: '1 Year'
      },
    },
  },
  engagement: {
    rollingEngagementMetrics: {
      totalHoursWatched: {
        data: 0,
        status: 'idle',
        error: null
      },
      totalMicrolearningsWatched: {
        data: 0,
        status: 'idle',
        error: null
      },
      totalCreditsClaimed: {
        data: 0,
        status: 'idle',
        error: null
      },
      mostPopular: {
        data: null,
        status: 'idle',
        error: null
      },
    },
    filters: {
      timeSpan: '90 Days',
      start_age: null,
      end_age: null,
      gender: 'All',
      department: 'All',
      role: 'All',
      years_in_profession: 'All'
    },
    mostWatched: {
      data: null,
      status: 'idle',
      error: null
    },
    mostClaimed: {
      data: null,
      status: 'idle',
      error: null
    },
  },
  efficacy: {
    baselineCorrelation: {
      data: null,
      status: 'idle',
      error: null,
    },
    accruedCorrelation: {
      data: null,
      status: 'idle',
      error: null
    },
    userMetrics: {
      filters: {
        timespan: '1 Year',
        age: 'All',
        gender: 'All',
        department: 'All',
        role: 'All',
        years_in_profession: 'All'
      },
      usersRegistered: {
        data: null,
        status: 'idle',
        error: null
      },
      microlearningsWatched: {
        data: null,
        status: 'idle',
        error: null
      },
    }
  }
};

const insightsSlice = createSlice({
  name: 'insights',
  initialState,
  reducers: {
    resetInsightsData: (state) => {
      state.reportedBurnout = initialState.reportedBurnout;
      state.engagement = initialState.engagement;
      state.efficacy = initialState.efficacy;
    },
    setOverallBurnoutMetricsFilters: (state, action) => {
      state.reportedBurnout.overallBurnoutMetrics.filters = {
        ...state.reportedBurnout.overallBurnoutMetrics.filters,
        ...action.payload,
      };
    },
    setSignificantBurnoutMetricsFilters: (state, action) => {
      state.reportedBurnout.significantBurnoutMetrics.filters = {
        ...state.reportedBurnout.significantBurnoutMetrics.filters,
        ...action.payload,
      };
    },
    setEngagementFilters: (state, action) => {
      state.engagement.filters = {
        ...state.engagement.filters,
        ...action.payload,
      };
    },
    setEfficacyUserFilters: (state, action) => {
      state.efficacy.userMetrics.filters = {
        ...state.efficacy.userMetrics.filters,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Reported Burnout
      .addCase(fetchRollingBurnoutMetrics.pending, (state) => {
        state.reportedBurnout.rollingBurnoutMetrics.status = 'loading';
        state.reportedBurnout.rollingBurnoutMetrics.error = null;
      })
      .addCase(fetchRollingBurnoutMetrics.fulfilled, (state, action) => {
        state.reportedBurnout.rollingBurnoutMetrics.status = 'succeeded';
        state.reportedBurnout.rollingBurnoutMetrics.data = action.payload;
      })
      .addCase(fetchRollingBurnoutMetrics.rejected, (state, action) => {
        state.reportedBurnout.rollingBurnoutMetrics.status = 'failed';
        state.reportedBurnout.rollingBurnoutMetrics.error = action.payload || action.error.message;
      })
      .addCase(fetchOverallBurnoutMetrics.pending, (state) => {
        state.reportedBurnout.overallBurnoutMetrics.status = 'loading';
        state.reportedBurnout.overallBurnoutMetrics.error = null;
      })
      .addCase(fetchOverallBurnoutMetrics.fulfilled, (state, action) => {
        state.reportedBurnout.overallBurnoutMetrics.status = 'succeeded';
        state.reportedBurnout.overallBurnoutMetrics.data = action.payload;
      })
      .addCase(fetchOverallBurnoutMetrics.rejected, (state, action) => {
        state.reportedBurnout.overallBurnoutMetrics.status = 'failed';
        state.reportedBurnout.overallBurnoutMetrics.error = action.payload || action.error.message;
      })
      .addCase(fetchTopStressors.pending, (state) => {
        state.reportedBurnout.topStressors.status = 'loading';
        state.reportedBurnout.topStressors.error = null;
      })
      .addCase(fetchTopStressors.fulfilled, (state, action) => {
        state.reportedBurnout.topStressors.status = 'succeeded';
        state.reportedBurnout.topStressors.comments = action.payload.comments;
        state.reportedBurnout.topStressors.topStressors = action.payload.top_stressors;
        state.reportedBurnout.topStressors.totalStressors = action.payload.total_stressors;
      })
      .addCase(fetchTopStressors.rejected, (state, action) => {
        state.reportedBurnout.topStressors.status = 'failed';
        state.reportedBurnout.topStressors.error = action.payload || action.error.message;
      })
      .addCase(fetchSignificantBurnoutMetrics.pending, (state) => {
        state.reportedBurnout.significantBurnoutMetrics.status = 'loading';
        state.reportedBurnout.significantBurnoutMetrics.error = null;
      })
      .addCase(fetchSignificantBurnoutMetrics.fulfilled, (state, action) => {
        state.reportedBurnout.significantBurnoutMetrics.status = 'succeeded';
        state.reportedBurnout.significantBurnoutMetrics.data = action.payload;
      })
      .addCase(fetchSignificantBurnoutMetrics.rejected, (state, action) => {
        state.reportedBurnout.significantBurnoutMetrics.status = 'failed';
        state.reportedBurnout.significantBurnoutMetrics.error = action.payload || action.error.message;
      })
      // Engagement
      .addCase(fetchRollingEngagementHours.pending, (state) => {
        state.engagement.rollingEngagementMetrics.totalHoursWatched.status = 'loading';
        state.engagement.rollingEngagementMetrics.totalHoursWatched.error = null;
      })
      .addCase(fetchRollingEngagementHours.fulfilled, (state, action) => {
        state.engagement.rollingEngagementMetrics.totalHoursWatched.status = 'succeeded';
        state.engagement.rollingEngagementMetrics.totalHoursWatched.data = action.payload;
      })
      .addCase(fetchRollingEngagementHours.rejected, (state, action) => {
        state.engagement.rollingEngagementMetrics.totalHoursWatched.status = 'failed';
        state.engagement.rollingEngagementMetrics.totalHoursWatched.error = action.payload || action.error.message;
      })
      .addCase(fetchRollingEngagementMicrolearnings.pending, (state) => {
        state.engagement.rollingEngagementMetrics.totalMicrolearningsWatched.status = 'loading';
        state.engagement.rollingEngagementMetrics.totalMicrolearningsWatched.error = null;
      })
      .addCase(fetchRollingEngagementMicrolearnings.fulfilled, (state, action) => {
        state.engagement.rollingEngagementMetrics.totalMicrolearningsWatched.status = 'succeeded';
        state.engagement.rollingEngagementMetrics.totalMicrolearningsWatched.data = action.payload;
      })
      .addCase(fetchRollingEngagementMicrolearnings.rejected, (state, action) => {
        state.engagement.rollingEngagementMetrics.totalMicrolearningsWatched.status = 'failed';
        state.engagement.rollingEngagementMetrics.totalMicrolearningsWatched.error = action.payload || action.error.message;
      })
      .addCase(fetchRollingEngagementCreditsClaimed.pending, (state) => {
        state.engagement.rollingEngagementMetrics.totalCreditsClaimed.status = 'loading';
        state.engagement.rollingEngagementMetrics.totalCreditsClaimed.error = null;
      })
      .addCase(fetchRollingEngagementCreditsClaimed.fulfilled, (state, action) => {
        state.engagement.rollingEngagementMetrics.totalCreditsClaimed.status = 'succeeded';
        state.engagement.rollingEngagementMetrics.totalCreditsClaimed.data = action.payload;
      })
      .addCase(fetchRollingEngagementCreditsClaimed.rejected, (state, action) => {
        state.engagement.rollingEngagementMetrics.totalCreditsClaimed.status = 'failed';
        state.engagement.rollingEngagementMetrics.totalCreditsClaimed.error = action.payload || action.error.message;
      })
      .addCase(fetchRollingEngagementPopularSeries.pending, (state) => {
        state.engagement.rollingEngagementMetrics.mostPopular.status = 'loading';
        state.engagement.rollingEngagementMetrics.mostPopular.error = null;
      })
      .addCase(fetchRollingEngagementPopularSeries.fulfilled, (state, action) => {
        state.engagement.rollingEngagementMetrics.mostPopular.status = 'succeeded';
        state.engagement.rollingEngagementMetrics.mostPopular.data = action.payload;
      })
      .addCase(fetchRollingEngagementPopularSeries.rejected, (state, action) => {
        state.engagement.rollingEngagementMetrics.mostPopular.status = 'failed';
        state.engagement.rollingEngagementMetrics.mostPopular.error = action.payload || action.error.message;
      })
      .addCase(fetchEngagementMostWatchedMicrolearnings.pending, (state) => {
        state.engagement.mostWatched.status = 'loading';
        state.engagement.mostWatched.error = null;
      })
      .addCase(fetchEngagementMostWatchedMicrolearnings.fulfilled, (state, action) => {
        state.engagement.mostWatched.status = 'succeeded';
        state.engagement.mostWatched.data = action.payload;
      })
      .addCase(fetchEngagementMostWatchedMicrolearnings.rejected, (state, action) => {
        state.engagement.mostWatched.status = 'failed';
        state.engagement.mostWatched.error = action.payload || action.error.message;
      })
      .addCase(fetchEngagementMostClaimedMicrolearnings.pending, (state) => {
        state.engagement.mostClaimed.status = 'loading';
        state.engagement.mostClaimed.error = null;
      })
      .addCase(fetchEngagementMostClaimedMicrolearnings.fulfilled, (state, action) => {
        state.engagement.mostClaimed.status = 'succeeded';
        state.engagement.mostClaimed.data = action.payload;
      })
      .addCase(fetchEngagementMostClaimedMicrolearnings.rejected, (state, action) => {
        state.engagement.mostClaimed.status = 'failed';
        state.engagement.mostClaimed.error = action.payload || action.error.message;
      })
      // Efficacy
      .addCase(fetchEfficacyBaseline.pending, (state) => {
        state.efficacy.baselineCorrelation.status = 'loading';
        state.efficacy.baselineCorrelation.error = null;
      })
      .addCase(fetchEfficacyBaseline.fulfilled, (state, action) => {
        state.efficacy.baselineCorrelation.status = 'succeeded';
        state.efficacy.baselineCorrelation.data = action.payload;
      })
      .addCase(fetchEfficacyBaseline.rejected, (state, action) => {
        state.efficacy.baselineCorrelation.status = 'failed';
        state.efficacy.baselineCorrelation.error = action.payload || action.error.message;
      })
      .addCase(fetchEfficacyAccrued.pending, (state) => {
        state.efficacy.accruedCorrelation.status = 'loading';
        state.efficacy.accruedCorrelation.error = null;
      })
      .addCase(fetchEfficacyAccrued.fulfilled, (state, action) => {
        state.efficacy.accruedCorrelation.status = 'succeeded';
        state.efficacy.accruedCorrelation.data = action.payload;
      })
      .addCase(fetchEfficacyAccrued.rejected, (state, action) => {
        state.efficacy.accruedCorrelation.status = 'failed';
        state.efficacy.accruedCorrelation.error = action.payload || action.error.message;
      })
      .addCase(fetchEfficacyUsersRegistered.pending, (state) => {
        state.efficacy.userMetrics.usersRegistered.status = 'loading';
        state.efficacy.userMetrics.usersRegistered.error = null;
      })
      .addCase(fetchEfficacyUsersRegistered.fulfilled, (state, action) => {
        state.efficacy.userMetrics.usersRegistered.status = 'succeeded';
        state.efficacy.userMetrics.usersRegistered.data = action.payload;
      })
      .addCase(fetchEfficacyUsersRegistered.rejected, (state, action) => {
        state.efficacy.userMetrics.usersRegistered.status = 'failed';
        state.efficacy.userMetrics.usersRegistered.error = action.payload || action.error.message;
      })
      .addCase(fetchEfficacyMicrolearningsWatched.pending, (state) => {
        state.efficacy.userMetrics.microlearningsWatched.status = 'loading';
        state.efficacy.userMetrics.microlearningsWatched.error = null;
      })
      .addCase(fetchEfficacyMicrolearningsWatched.fulfilled, (state, action) => {
        state.efficacy.userMetrics.microlearningsWatched.status = 'succeeded';
        state.efficacy.userMetrics.microlearningsWatched.data = action.payload;
      });
  },
});

export const { resetInsightsData, setOverallBurnoutMetricsFilters, setSignificantBurnoutMetricsFilters, setEngagementFilters, setEfficacyUserFilters } = insightsSlice.actions;
export default insightsSlice.reducer;

export const getComments = (state) => {
  const comments = state.insights.reportedBurnout.topStressors.comments || [];
  return comments.filter(comment => comment.comment);
};